import React, { useState, useEffect } from 'react';
import { ArrowBigDown } from 'lucide-react';

interface WelcomeLoaderProps {
  onLoadingComplete: () => void;
}

const WelcomeLoader: React.FC<WelcomeLoaderProps> = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);

  const loadingSteps = [
    "Initializing Foundation...",
    "Loading Community Data...",
    "Preparing Resources...",
    "Almost Ready..."
  ];

  useEffect(() => {
    const duration = 2000; // 3 seconds
    const interval = 30; // Update every 30ms for smoother animation
    const increment = (interval / duration) * 100;

    const timer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + increment;
        
        // Update step based on progress
        const stepIndex = Math.floor((newProgress / 100) * loadingSteps.length);
        setCurrentStep(Math.min(stepIndex, loadingSteps.length - 1));
        
        if (newProgress >= 100) {
          clearInterval(timer);
          // Start fade out animation
          setTimeout(() => {
            setIsVisible(false);
            // Complete loading after fade out
            setTimeout(() => {
              onLoadingComplete();
            }, 800);
          }, 300);
          return 100;
        }
        return newProgress;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [onLoadingComplete]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 transition-all duration-800 ${!isVisible ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, hsl(var(--primary)) 0%, transparent 50%), 
                           radial-gradient(circle at 75% 75%, hsl(var(--accent)) 0%, transparent 50%)`,
          backgroundSize: '100px 100px',
          animation: 'float 6s ease-in-out infinite'
        }}></div>
      </div>

      <div className="relative text-center max-w-md mx-auto px-8">
        {/* Main Logo Container */}
        <div className="mb-12 relative">
          {/* Outer rotating ring */}
          <div className="absolute inset-0 w-32 h-32 mx-auto">
            <div className="w-full h-full rounded-full border-4 border-transparent border-t-primary border-r-primary/50 animate-spin"></div>
          </div>
          
          {/* Inner pulsing ring */}
          <div className="absolute inset-2 w-28 h-28 mx-auto">
            <div className="w-full h-full rounded-full border-2 border-accent/30 animate-pulse"></div>
          </div>
          
          {/* Logo */}
          <div className="relative w-32 h-32 mx-auto flex items-center justify-center">
            <div className="bg-gradient-to-br from-primary to-accent p-6 rounded-full shadow-2xl transform transition-transform duration-1000 hover:scale-110">
              <ArrowBigDown 
                className="text-white w-12 h-12 animate-bounce" 
                strokeWidth={2.5} 
              />
            </div>
          </div>

          {/* Floating orbs */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-3 h-3 bg-gradient-to-r from-primary to-accent rounded-full opacity-60"
                style={{
                  left: `${50 + 40 * Math.cos((i * Math.PI * 2) / 8)}%`,
                  top: `${50 + 40 * Math.sin((i * Math.PI * 2) / 8)}%`,
                  animation: `float ${3 + i * 0.2}s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`
                }}
              ></div>
            ))}
          </div>
        </div>

        {/* Brand Text */}
        <div className="mb-8 space-y-4">
          <div className="overflow-hidden">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent animate-fade-in-up">
              Arrow Drop
            </h1>
          </div>
          <div className="overflow-hidden">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              Foundation
            </h2>
          </div>
          <div className="overflow-hidden">
            <p className="text-lg text-muted-foreground animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              Building a Better Future Together
            </p>
          </div>
        </div>

        {/* Progress Section */}
        <div className="space-y-6">
          {/* Loading Step */}
          <div className="h-6 flex items-center justify-center">
            <p className="text-sm font-medium text-primary animate-pulse">
              {loadingSteps[currentStep]}
            </p>
          </div>

          {/* Progress Bar Container */}
          <div className="relative">
            {/* Background track */}
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden shadow-inner">
              {/* Progress fill */}
              <div 
                className="h-full bg-gradient-to-r from-primary via-accent to-primary rounded-full transition-all duration-300 ease-out relative overflow-hidden"
                style={{ width: `${progress}%` }}
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
              </div>
            </div>
            
            {/* Progress percentage */}
            <div className="flex justify-between items-center mt-3">
              <span className="text-xs text-muted-foreground">Loading</span>
              <span className="text-xs font-mono text-primary">
                {Math.round(progress)}%
              </span>
            </div>
          </div>

          {/* Loading dots */}
          <div className="flex justify-center space-x-2">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 bg-primary rounded-full animate-pulse"
                style={{ animationDelay: `${i * 0.2}s` }}
              ></div>
            ))}
          </div>
        </div>

        {/* Bottom decorative elements */}
        <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 w-64 h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
      </div>

      {/* Corner decorations */}
      <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-primary/20 rounded-tl-lg"></div>
      <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-primary/20 rounded-tr-lg"></div>
      <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-primary/20 rounded-bl-lg"></div>
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-primary/20 rounded-br-lg"></div>
    </div>
  );
};

export default WelcomeLoader;